/**
 * Nosto cart resync.
 *
 * Keeps Nosto's server-side view of the cart in step with Shopify's whenever the cart
 * changes without a full page reload. The `<nosto-campaign cart-synced>` placements
 * reload themselves off Nosto's `cartupdated` event, and Nosto's own Shopify script
 * already fires that for standard AJAX cart mutations — but only for surfaces whose
 * add/remove goes through the paths it intercepts, and only pushes the cart to Nosto's
 * server when a cart-synced campaign happens to be mounted and reloads. That leaves gaps:
 *
 *   - the wishlist ("Save for Later") add-to-cart dispatches only the theme's own
 *     `cart:refresh` (see _wishlist_add_to_cart_fix.js), which Nosto doesn't listen for;
 *   - a bulk change (e.g. cart cleared) that doesn't map to a tracked add/remove;
 *   - any change made while no cart-synced campaign is on the page, so the server never
 *     receives the new cart until the next navigation re-syncs it.
 *
 * This module is the belt-and-braces guarantee: on every theme cart event it reads the
 * WHOLE cart from /cart.js and pushes it to Nosto. Reading the full cart (rather than the
 * add/remove response, which only carries the affected line) is what stops the campaign
 * ever reflecting a single product instead of the basket.
 *
 * IMPORTANT: sync with update(), never load(). update() sends the fresh cart without a
 * page view, so repeat calls in a session don't inflate the campaign's impression/reach
 * stats. On the standard surfaces Nosto's native integration also syncs, so a given add
 * can round-trip twice — harmless precisely because update() is impression-safe.
 *
 * Fires on the theme's `cart:change` (native adds, quantity changes, removals) and
 * `cart:refresh` (wishlist, the Nosto carousel, and any programmatic mini-cart refresh),
 * both of which the theme itself listens for on `document`. A trailing debounce coalesces
 * the burst an add can emit (a `cart:change` immediately followed by a `cart:refresh`)
 * into one /cart.js read and one Nosto update.
 */

import { recommendationsEnabled } from './_nosto-flags.js';

const SYNC_DEBOUNCE_MS = 200;

// Match the attribution module's debug ergonomics so both can be driven the same way.
// Enable with `nostoCartSync.on()` (persists across navigations); `.off()` to stop.
const DEBUG_KEY = 'nostoCartSyncDebug';

function debugEnabled() {
  if (window.nostoCartSyncDebug) return true;
  try {
    return window.localStorage.getItem(DEBUG_KEY) === 'true';
  } catch (e) {
    return false; // storage can throw in private mode / blocked-cookie contexts
  }
}

function setDebug(on) {
  try {
    if (on) window.localStorage.setItem(DEBUG_KEY, 'true');
    else window.localStorage.removeItem(DEBUG_KEY);
  } catch (e) {
    window.nostoCartSyncDebug = on;
  }
  console.log('[nosto-cart-sync] debug ' + (on ? 'ON — change the cart' : 'off'));
}

function debug(msg, data) {
  if (!debugEnabled()) return;
  if (data === undefined) console.log('[nosto-cart-sync] ' + msg);
  else console.log('[nosto-cart-sync] ' + msg, data);
}

// Only exposed while the feature is on, so a storefront with Nosto switched off has no
// hand-holds that would push cart data to Nosto if someone called them from a console.
if (recommendationsEnabled()) {
  window.nostoCartSync = {
    on: () => setDebug(true),
    off: () => setDebug(false),
    // Exposed so the sync can be triggered by hand (tests, or a surface that mutates the
    // cart without emitting either theme event).
    sync: () => updateNostoCart()
  };
}

/**
 * Map a Shopify /cart.js line to the Nosto cart-item shape. `product_id` is the product
 * and `variant_id` the SKU; prices come back in the store's minor units (pence), so /100
 * gives the major-unit value Nosto expects. `cart.currency` is the ISO code (e.g. "GBP").
 */
function toNostoItem(item, currency) {
  return {
    product_id: String(item.product_id),
    sku_id: String(item.variant_id),
    quantity: item.quantity,
    name: item.product_title,
    unit_price: (item.price / 100).toFixed(2),
    price_currency_code: currency
  };
}

let inFlight = false;
function updateNostoCart() {
  // Nosto's script is injected by the Shopify app with no theme-side stub, so a consent
  // tool (Pandectes) or an ad blocker can keep it out entirely. Bail rather than throw.
  if (typeof window.nostojs !== 'function') {
    debug('skipping — window.nostojs is absent (blocked by consent tool or ad blocker?)');
    return;
  }
  if (inFlight) {
    debug('skipping — a sync is already in flight');
    return;
  }
  inFlight = true;

  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((res) => res.json())
    .then((cart) => {
      const items = (cart.items || []).map((item) => toNostoItem(item, cart.currency));
      debug('syncing ' + items.length + ' line(s) to Nosto', items);

      window.nostojs((api) => {
        // Update the tagging provider so any *subsequent* Nosto event this page sends
        // carries the current cart, then push the cart now with an impression-safe update.
        api.setTaggingProvider('cart', { items });
        api
          .defaultSession()
          .setCart({ items })
          .viewCart()
          .update();
        debug('sync sent (defaultSession().setCart().viewCart().update())');
      });
    })
    .catch((e) => debug('sync failed — could not read /cart.js', e))
    .finally(() => {
      inFlight = false;
    });
}

let debounceTimer = null;
function scheduleSync(reason) {
  debug('cart event: ' + reason + ' → sync scheduled');
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(updateNostoCart, SYNC_DEBOUNCE_MS);
}

let bound = false;
function init() {
  if (bound) return;
  bound = true;

  // Both events bubble to / are dispatched at document — the same node the theme's own
  // cart-drawer refresh listens on — so a single document-level listener catches every
  // surface without needing to know which element mutated the cart.
  document.addEventListener('cart:change', () => scheduleSync('cart:change'));
  document.addEventListener('cart:refresh', () => scheduleSync('cart:refresh'));
}

// Off unless the "Nosto recommendations" theme setting is on. This is the one Nosto
// module that actively pushes data on every cart mutation, so it must not run while
// Nosto is switched off — the <nosto-campaign cart-synced> placements it feeds aren't
// on the page anyway. See _nosto-flags.js.
if (recommendationsEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
